import DateInput from "@/components/FormControl/Date/DateInput";
import Select from "@/components/FormControl/Select/Select";
import PaginatedSelect from "@/components/FormControl/Select/PaginatedSelect";
import { useAppDispatch } from "@/store/hooks";
import type { RootState } from "@/store/rootReducer";
import { getAssetItemNames, getTypeNames } from "@/store/slices/assetManagementSlice";
import { getCityList, getLocalityList, getStateList } from "@/store/slices/customerSlice";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Popover, TextField } from "@mui/material";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { type appliedFilter, type list, type showFilters, EMPTY_LOCATION, INITIAL_FILTERS } from "./FilterInterface";
import { DATE_FILTER_CONFIG, FILTER_CONFIG, GEOFENCE_FILTER_CONFIG, LOCATION_FIELDS, SPECIAL_SELECT_CONFIG, TEXT_FILTER_CONFIG } from "./FilterConfig";

type FilterKey = Exclude<keyof showFilters, "location">;

interface FilterProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: any) => void;
  defaultDateType?: string;
  filterName?: string;
  width?: string;
  appliedFilter: appliedFilter;
  showFilters?: showFilters;
  list?: list;
  /** Paginated customer/lead fetch for filter dropdown (e.g. Deal & Pipeline). Other modules omit this. */
  onFetchCustomerPage?: (
    page: number,
    search: string
  ) => Promise<{ items: any[]; total: number }>;
  defaultDateLabel?: string;
}

const normalizeValue = (val: any): any => {
  if (Array.isArray(val)) return val.map((v) => normalizeValue(v)).sort();
  if (dayjs.isDayjs(val)) return val.format("YYYY-MM-DD");

  if (val && typeof val === "object") {
    const obj: any = {};
    Object.keys(val).forEach((key) => {
      obj[key] = normalizeValue(val[key]);
    });
    return obj;
  }

  return val ?? null;
};

const buildComparableFilter = (data: any) => ({
  ...INITIAL_FILTERS,
  ...data,
  fromDate: data?.fromDate
    ? dayjs(data.fromDate).format("YYYY-MM-DD")
    : null,
  toDate: data?.toDate
    ? dayjs(data.toDate).format("YYYY-MM-DD")
    : null,
  fromYear: data?.fromYear
    ? dayjs(data.fromYear).format("YYYY")
    : null,
  toYear: data?.toYear
    ? dayjs(data.toYear).format("YYYY")
    : null,
});

const FILTER_GRID_MAP = {
  customer: 4,

  secondarySalesOrder: 3,
  users: 3,
  assetLocation: 3,
  expenseApproval: 3,
  myExpenseClaim: 3,
  opportunity: 3,

  salesOrder: 2,
  order: 2,
  callDetails: 2,
  dealsAndPipelines: 2,
  callView: 2,
  customerView: 2,
  dealsAndPipelinesFollowUps: 2,
  dealsLostReason: 2,
  leadAgainstAccount: 2,
  opportunityAgainstLead: 2,
  salePerformanceViewDetails: 2,
  tagOpportunityView: 2,
  taskDetailsView: 2,
  rosterPlan: 2,
  teamClaim: 2,
  asset: 2,
  assetAllocation: 2,
  userSeparation: 2,
  visitCalendar: 2,
  devicechangeLogs: 2,
  expenseApprover: 2,
  locationCoverage: 2,
  leaveManagement: 2,
  GeofenceManagement: 2,
  timeline: 2,
  teamList: 2,
  attendanceList: 2,
};

const GRID_CLASSES: Record<number, string> = {
  1: "grid gap-4 grid-cols-1",
  2: "grid gap-4 grid-cols-2",
  3: "grid gap-4 grid-cols-3",
  4: "grid gap-4 grid-cols-4",
};

const getFilterGridClassName = (filterName?: string) => {
  const cols = FILTER_GRID_MAP[filterName as keyof typeof FILTER_GRID_MAP] || 1;
  return GRID_CLASSES[cols];
};

const getLocationGridTemplateColumns = (filterName?: string): string => {
  if (filterName === "customer") return "repeat(4, 1fr)";
  if (filterName === "order") return "repeat(3, 1fr)";
  return "repeat(2, 1fr)";
};

const getTextGridTemplateColumns = (filterName?: string): string => {
  if (filterName === "secondarySalesOrder" || filterName === "order") return "grid gap-4 grid-cols-2";
  return "grid gap-4 grid-cols-1";
};

const sectionLabelClassName = "mb-1 font-medium text-[#212529] text-[13px] font-['poppins']";

const GenericSelectFilter = memo(({
  config,
  filters,
  updateFilter,
  options,
  filterName,
  customOnChange,
  onFetchCustomerPage,
  paginatedSelectResetKey,
  locationClassName,
  hideLabel,
  showFilter,
}: any) => {
  const title = config.getTitle?.(filterName) || config.title;
  const label = config.getLabel?.(filterName) || config.label;
  const field = {
    value: filters?.[config.filterKey],
    onChange: (v: any) =>
      customOnChange
        ? customOnChange(v)
        : updateFilter(config.filterKey, v),
  };

  const isMulti = showFilter?.mode === "multi";

  return (
    <div className={`${locationClassName ? locationClassName : "px-3 py-5"}`}>
      {!hideLabel && (
        <p className={sectionLabelClassName}>
          {title}
        </p>
      )}

      {config.usePaginatedSelect ? (
        <PaginatedSelect
          key={paginatedSelectResetKey}
          resetKey={paginatedSelectResetKey}
          label={label}
          field={field}
          options={options}
          onFetchPage={onFetchCustomerPage}
          idKey={config.idKey}
          nameKey={config.nameKey}
        />
      ) : (
        <Select
          label={label}
          field={field}
          options={options}
          idKey={config.idKey}
          nameKey={config.nameKey}
          disabled={config.disabled?.(filters)}
          isMulti={isMulti}
          returnObject
          enableSearch={config.enableSearch ?? true}
        />
      )}
    </div>
  );
});

const GenericDateFilter = memo(({
  title,
  label,
  filterKey,
  filters,
  updateFilter,
}: any) => (
  <div className="px-3 py-5">
    <p className={sectionLabelClassName}>{title}</p>

    <DateInput
      label={label}
      field={{
        value: filters?.[filterKey],
        onChange: (v: any) =>
          updateFilter(filterKey, v),
      }}
      format="DD MMM, YYYY"
      maxDate={dayjs()}
    />
  </div>
));

const GenericTextFilter = memo(({
  title,
  placeholder,
  filterKey,
  type = "text",
  filters,
  updateFilter,
}: any) => (
  <FilterSection title={title} className="px-3 py-5" titleClassName={`${sectionLabelClassName} mb-6`}>
    <TextField
      variant="standard"
      fullWidth
      type={type}
      placeholder={placeholder}
      value={filters?.[filterKey]}
      onChange={(e) =>
        updateFilter(filterKey, e.target.value)
      }
    />
  </FilterSection>
));

const FilterSection = memo(({
  title,
  children,
  className = "px-3 py-5",
  titleClassName = sectionLabelClassName,
}: {
  title?: string;
  children: ReactNode;
  className?: string;
  titleClassName?: string;
}) => (
  <div className={className}>
    {title ? <p className={titleClassName}>{title}</p> : null}
    {children}
  </div>
));

const FilterComponent: React.FC<FilterProps> = ({
  anchorEl,
  onClose,
  onApply,
  filterName,
  width,
  defaultDateType,
  appliedFilter,
  showFilters,
  list,
  onFetchCustomerPage,
  defaultDateLabel,
}) => {
  const open = Boolean(anchorEl);

  const dateTypeOptions = useMemo(
    () => [
      {
        label: "Create Date",
        value: filterName === "customer" || filterName === "mytask" ? "CreatedDate" : "CreateDate",
      },
      { label: "Modified Date", value: "ModifiedDate" },
      ...((filterName !== "customer" && filterName !== "users" && filterName !== "userAuditLog" && filterName !== "mytask") ? [{ label: "Close Date", value: "CloseDate" }] : []),
    ],
    [filterName]
  );

  const dispatch = useAppDispatch();
  const { stateList, cityList, localityList } = useSelector((state: RootState) => state.customer);
  const { typeIdsList, assetItemIdsList } = useSelector((state: RootState) => state.asset);

  const [filters, setFilters] = useState<any>(INITIAL_FILTERS);
  const [dateError, setDateError] = useState<string>("");
  const [yearError, setYearError] = useState<string>("");
  const [paginatedSelectResetKey, setPaginatedSelectResetKey] = useState(0);

  const isLeaveModule = filterName === "leaveManagement";

  const defaultDateTypeOption = useMemo(() => {
    return (
      dateTypeOptions.find(
        (item) => item.value === defaultDateType
      ) || null
    );
  }, [dateTypeOptions, defaultDateType]);

  const updateFilter = useCallback((key: keyof appliedFilter, value: any) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
  }, []);

  const updateDateFilter = useCallback((key: keyof appliedFilter, value: any) => {
    setDateError("");
    updateFilter(key, value);
  }, [updateFilter]);

  const updateYearFilter = useCallback((key: keyof appliedFilter, value: any) => {
    setYearError("");
    updateFilter(key, value);
  }, [updateFilter]);

  const specialOptionsMap = useMemo(() => ({
    typeIdsList,
    assetItemIdsList,
  }), [typeIdsList, assetItemIdsList]);

  const locationOptionsMap = useMemo(
    () => ({
      countryList: list?.countryList || [],
      stateList,
      cityList,
      localityList,
    }),
    [list?.countryList, stateList, cityList, localityList]
  );

  const hasVisibleLocationFilter = useMemo(
    () => LOCATION_FIELDS.some((field) => field.show(showFilters)),
    [showFilters]
  );

  const locationFields = useMemo(
    () => LOCATION_FIELDS.filter((field) => field.show(showFilters)),
    [showFilters]
  );

  const dateFilterEntries = useMemo(() => Object.entries(DATE_FILTER_CONFIG), []);
  const filterEntries = useMemo(() => Object.entries(FILTER_CONFIG), []);
  const specialFilterEntries = useMemo(() => Object.entries(SPECIAL_SELECT_CONFIG), []);
  const textFilterEntries = useMemo(() => Object.entries(TEXT_FILTER_CONFIG), []);

  const getVisibleFilterEntries = useCallback(
    (entries: [string, any][]) => entries.filter(([key]) => showFilters?.[key as FilterKey]?.show),
    [showFilters]
  );

  useEffect(() => {
    if (!open) {
      setPaginatedSelectResetKey((k) => k + 1);
      return;
    }

    setFilters({
      ...INITIAL_FILTERS,
      ...appliedFilter,
      dateType: appliedFilter?.dateType || defaultDateTypeOption,
      fromDate: appliedFilter?.fromDate ? dayjs(appliedFilter.fromDate) : null,
      toDate: appliedFilter?.toDate ? dayjs(appliedFilter.toDate) : null,
      fromYear: appliedFilter?.fromYear ? dayjs(appliedFilter.fromYear) : null,
      toYear: appliedFilter?.toYear ? dayjs(appliedFilter.toYear) : null,
      location: appliedFilter?.location || EMPTY_LOCATION,
    });

    setDateError("");
    setYearError("");
  }, [open, appliedFilter, defaultDateTypeOption]);

  useEffect(() => {
    const countryId = filters.location?.country?.CountryId;
    if (countryId) dispatch(getStateList(countryId));
  }, [filters.location?.country?.CountryId, dispatch]);

  useEffect(() => {
    const stateId = filters.location?.state?.StateId;
    const shouldLoadCity = showFilters?.location?.city?.show;

    if (stateId && shouldLoadCity) dispatch(getCityList(stateId));
  }, [filters.location?.state?.StateId, showFilters?.location?.city?.show, dispatch]);

  useEffect(() => {
    const cityId = filters.location?.city?.CityId;
    const shouldLoadLocality = showFilters?.location?.locality?.show;

    if (cityId && shouldLoadLocality) {
      dispatch(getLocalityList(cityId));
    }
  }, [filters.location?.city?.CityId, showFilters?.location?.locality?.show, dispatch]);

  useEffect(() => {
    const groupId = filters?.groupId?.Id;
    if (groupId && showFilters?.type?.show) dispatch(getTypeNames({ groupId: groupId }));
  }, [filters?.groupId?.Id, showFilters?.type?.show, dispatch]);

  useEffect(() => {
    const typeId = filters?.typeId?.TypeId;
    if (typeId && (showFilters?.item?.show)) dispatch(getAssetItemNames({ typeId: typeId }));
  }, [filters?.typeId?.TypeId, showFilters?.item?.show, dispatch]);

  const comparableFilters = useMemo(
    () => normalizeValue(buildComparableFilter(filters)),
    [filters]
  );

  const comparableAppliedFilters = useMemo(
    () => normalizeValue(buildComparableFilter(appliedFilter)),
    [appliedFilter]
  );

  const isFilterChanged = useMemo(() => {
    return JSON.stringify(comparableFilters) !== JSON.stringify(comparableAppliedFilters);
  }, [comparableFilters, comparableAppliedFilters]);

  // clear
  const handleClear = () => {
    const cleared = {
      ...INITIAL_FILTERS,
      dateType: defaultDateTypeOption,
    };

    setFilters(cleared);
    onApply(cleared);
    onClose();
  };

  // Apply
  const handleApply = () => {
    if (filters.fromDate && !filters.toDate) {
      setDateError("Please select To Date");
      return;
    }

    if (filters.fromDate && filters.toDate && filters.toDate.isBefore(filters.fromDate, "day")) {
      return setDateError("To Date cannot be earlier than From Date");
    }

    if (filters.fromYear && !filters.toYear) {
      setYearError("Please select To Year");
      return;
    }

    if (filters.fromYear && filters.toYear && filters.toYear.isBefore(filters.fromYear, "year")) {
      setYearError("To Year cannot be earlier than From Year");
      return;
    }

    if (!isFilterChanged) return onClose();

    onApply({
      ...filters,
      dateType: filters.dateType || "",
    });

    onClose();
  };

  const dateTypeValue = useMemo(() => {
    return typeof filters.dateType === "object" ? filters.dateType?.value : null;
  }, [filters.dateType]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <div className="p-2" style={{ width: width || "400px" }}>
        {/* Close Button */}
        <div className="flex justify-end">
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Date Section */}
        {showFilters?.date?.show && (
          <FilterSection title={defaultDateLabel || "By Date"}>
            <div className={`grid gap-4 ${(!showFilters?.dateType?.show) ? "grid-cols-2" : "grid-cols-3"}`}>
              {/* Date Type */}
              {showFilters?.dateType?.show && (
                <Select
                  label="Date Type"
                  field={{
                    value: filters.dateType,
                    onChange: (e: any) => setFilters((p: any) => ({ ...p, dateType: e }))
                  }}
                  options={dateTypeOptions || []}
                  idKey="value"
                  nameKey="label"
                  returnObject
                  enableSearch={false}
                />
              )}

              {/* From Date */}
              <DateInput
                field={{
                  value: filters.fromDate,
                  onChange: (v: any) => updateDateFilter("fromDate", v)
                }}
                label="From Date"
                format="DD MMM, YYYY"
                {...(!isLeaveModule && {
                  maxDate: dayjs()
                })}
                slotProps={{ textField: { variant: "standard", fullWidth: true, placeholder: "From Date" } }}
              />

              {/* To Date */}
              <DateInput
                label="To Date"
                field={{
                  value: filters.toDate,
                  onChange: (v: any) => updateDateFilter("toDate", v)
                }}
                minDate={dayjs(filters.fromDate) || undefined}
                format="DD MMM, YYYY"
                {...(!isLeaveModule && {
                  disableFuture: dateTypeValue !== "CloseDate",
                  ...(filterName !== "order" &&
                    filterName !== "visitForm" &&
                    dateTypeValue !== "CloseDate" && {
                    maxDate: dayjs(),
                  }),
                })}
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                    placeholder: "To Date",
                    error: !!dateError,
                    helperText: dateError,
                  },
                }}
              />
            </div>

            {showFilters?.stage?.show && filterName === "customerView" && (
              <div className="mt-4">
                <p className={sectionLabelClassName}>By Stage</p>
                <Select
                  label="Stage"
                  field={{
                    value: filters?.stageIds?.[0] || null,
                    onChange: (v: any) =>
                      setFilters((p: any) => ({
                        ...p,
                        stageIds: v ? [v] : [],
                      }))
                  }}
                  options={list?.stageList || []}
                  idKey="Id"
                  nameKey="Name"
                  returnObject
                />
              </div>
            )}
          </FilterSection>
        )}

        {/* Year Section */}
        {showFilters?.year?.show && (
          <FilterSection title="By Qualification">
            <div className={`grid ${showFilters.qualification?.show ? "grid-cols-3" : "grid-cols-2"} gap-4`}>
              {/* Qualification Filter */}
              {showFilters?.qualification?.show && (
                <Select
                  label="Qualification"
                  field={{
                    value: filters?.qualification,
                    onChange: (v: any) => setFilters((p: any) => ({ ...p, qualification: v }))
                  }}
                  options={list?.qualificationList || []}
                  idKey="Qualification"
                  nameKey="Qualification"
                  returnObject
                />
              )}

              {/* From Year */}
              <DateInput
                views={["year"]}
                label="From Year"
                field={{
                  value: filters.fromYear,
                  onChange: (v: any) => updateYearFilter("fromYear", v)
                }}
                format="YYYY"
                maxDate={dayjs()}
                slotProps={{
                  textField: { variant: "standard", fullWidth: true, placeholder: "From Year" }
                }}
              />

              {/* To Year */}
              <DateInput
                views={["year"]}
                label="To Year"
                field={{
                  value: filters.toYear,
                  onChange: (v: any) => updateYearFilter("toYear", v)
                }}
                minDate={filters.fromYear || undefined}
                format="YYYY"
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                    placeholder: "To Year",
                    error: !!yearError,
                    helperText: yearError,
                  },
                }}
              />
            </div>
          </FilterSection>
        )}

        {/* location filter */}
        {hasVisibleLocationFilter && (
          <FilterSection title="By Location" className="py-5" titleClassName={`${sectionLabelClassName} px-3`}>
            <Box
              display="grid"
              gridTemplateColumns={getLocationGridTemplateColumns(filterName)}
              gap={2}
              sx={{
                "& > *": {
                  minWidth: 0,
                },
              }}
            >
              {locationFields.map((field) => (
                <GenericSelectFilter
                  key={field.key}
                  locationClassName="px-3"
                  config={{
                    label: field.label,
                    filterKey: field.key,
                    idKey: field.idKey,
                    nameKey: field.nameKey,
                    disabled: () => field.dependsOn ? !filters?.location?.[field.dependsOn] : false,
                  }}
                  filters={filters.location}
                  options={locationOptionsMap[field.optionSource as keyof typeof locationOptionsMap]}
                  customOnChange={(value: any) => {
                    setFilters((prev: any) => {
                      const location = {
                        ...prev.location,
                        [field.key]: value,
                      };

                      field.reset?.forEach((k) => {
                        location[k] = null;
                      });

                      return {
                        ...prev,
                        location,
                      };
                    });
                  }}
                />
              ))}
            </Box>
          </FilterSection>
        )}

        <div className={getFilterGridClassName(filterName)}>
          {getVisibleFilterEntries(dateFilterEntries).map(([key, config]) => (
            <GenericDateFilter
              key={key}
              {...config}
              filterKey={key}
              filters={filters}
              updateFilter={updateFilter}
            />
          ))}

          {getVisibleFilterEntries(filterEntries).map(([key, config]) => {
            const visibility = showFilters?.[key as FilterKey];
            const optionKey = config.optionKey as keyof NonNullable<list>;
            const data = list?.[optionKey];

            const options = Array.isArray(data)
              ? config.transformOptions
                ? data.map((item: string, index: number) => ({
                  Id: index,
                  Name: item,
                }))
                : data
              : [];

            return (
              <GenericSelectFilter
                key={key}
                config={config}
                filters={filters}
                updateFilter={updateFilter}
                options={options}
                filterName={filterName}
                showFilter={visibility}
                {...(config.usePaginatedSelect && {
                  onFetchCustomerPage,
                  paginatedSelectResetKey,
                })}
              />
            );
          })}

          {getVisibleFilterEntries(specialFilterEntries).map(([key, config]) => (
            <GenericSelectFilter
              key={key}
              config={config}
              filters={filters}
              updateFilter={updateFilter}
              showFilter={showFilters?.[key as FilterKey]}
              options={
                specialOptionsMap[
                config.optionSource as keyof typeof specialOptionsMap
                ] || []
              }
            />
          ))}
        </div>

        <div className={getTextGridTemplateColumns(filterName)}>
          {getVisibleFilterEntries(textFilterEntries).map(([key, config]) => (
            <GenericTextFilter
              key={key}
              {...config}
              filters={filters}
              updateFilter={updateFilter}
            />
          ))}
        </div>

        {/* Be GeoFenceType */}
        {showFilters?.geoFenceType?.show && (
          <FilterSection title="By Geofence Type" className="py-5" titleClassName={`${sectionLabelClassName} px-3`}>
            <div className="grid gap-4 grid-cols-2">
              {GEOFENCE_FILTER_CONFIG
                .filter((config) => config.type === "geoFenceType")
                .map((config) => (
                  <GenericSelectFilter
                    key={config.filterKey}
                    config={config}
                    filters={filters}
                    updateFilter={updateFilter}
                    options={list?.geoFenceList || []}
                    showFilter={showFilters?.geoFenceType}
                    hideLabel
                  />
                ))}
            </div>
          </FilterSection>
        )}

        {/* Be GeoFenceName */}
        {showFilters?.geoFenceName?.show && (
          <FilterSection title="By Geofence Name" className="py-5" titleClassName={`${sectionLabelClassName} px-3`}>
            <div className="grid gap-4 grid-cols-2">
              {GEOFENCE_FILTER_CONFIG
                .filter((config) => config.type === "geoFenceName")
                .map((config) => (
                  <GenericSelectFilter
                    key={config.filterKey}
                    config={config}
                    filters={filters}
                    updateFilter={updateFilter}
                    options={list?.geoFenceNameList || []}
                    showFilter={showFilters?.geoFenceName}
                    hideLabel
                  />
                ))}
            </div>
          </FilterSection>
        )}

        {/* By Configuration Status */}
        {showFilters?.settingType?.show && (
          <FilterSection title="By Configuration Status" className="py-5" titleClassName={`${sectionLabelClassName} px-3`}>
            <div className="grid gap-4 grid-cols-2">
              <GenericSelectFilter
                config={{
                  label: "Type",
                  filterKey: "settingType",
                  idKey: "value",
                  nameKey: "label",
                }}
                filters={filters}
                updateFilter={updateFilter}
                customOnChange={(v: any) => {
                  updateFilter("settingType", v);
                  if (v) {
                    if (!filters?.settingStatus) {
                      updateFilter("settingStatus", { label: "Enable", value: true });
                    }
                  } else {
                    updateFilter("settingStatus", null);
                  }
                }}
                options={list?.settingTypeList || []}
                showFilter={showFilters?.settingType}
                hideLabel
              />
              <GenericSelectFilter
                config={{
                  label: "Type Status",
                  filterKey: "settingStatus",
                  idKey: "value",
                  nameKey: "label",
                  disabled: (f: any) => !f?.settingType,
                }}
                filters={filters}
                updateFilter={updateFilter}
                options={list?.settingStatusList || [
                  { label: "Enable", value: true },
                  { label: "Disable", value: false },
                ]}
                showFilter={showFilters?.settingType}
                hideLabel
              />
            </div>
          </FilterSection>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-4 py-4">
          <Button variant="contained" onClick={handleClear} style={{ color: "black", backgroundColor: "white" }}>
            Clear
          </Button>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export default memo(FilterComponent);
